import { useState, useCallback } from 'react';
import { t } from '@lingui/macro';
import styled from '@emotion/styled';
import { Autocomplete, createFilterOptions, type FilterOptionsState } from '@mui/material';
import { TextInput, TextInputProps } from './TextInput';
import type { ComboBoxProps, ComboBoxOption } from './types';
import { useGet } from 'lib/api';
import { debounce } from 'lodash';
import { lighten } from '@mui/system';
import { Tooltip } from 'components/tooltip';
import { Avatar } from 'components/avatar';

export const parseSearch = (atSearch: boolean, search: string) => {
  if (atSearch) {
    if (search.charAt(0) === '@') {
      return search.substring(1).trim();
    }
    return '';
  }
  return search.trim();
};

export const ComboBox = ({
  sx,
  options = [],
  label,
  placeholder,
  clearOnBlur = true,
  openText = t`Open`,
  loading = false,
  fullWidth = false,
  loadingText = t`Loading...`,
  noOptionsText = t`No options`,
  inputVariant = 'unstyled',
  freeSolo = true,
  InputProps,
  error,
  onBlur,
  onChange,
  onClose,
  onInputChange,
  open,
  api = null,
  optionsLimit,
  onKeyDown,
  onKey,
  groupBy = false,
  atSearch = false,
  hasAvatar = false,
  ...rest
}: ComboBoxProps) => {
  const [search, setSearch] = useState('');
  const { data } = useGet(
    api?.url || null,
    api?.search ? [api?.key, search] : api?.key || '',
    api?.search ? [parseSearch(atSearch, search), ...(api?.vars || [])] : [],
    undefined,
    api?.revalidate !== false,
    {
      enabled: api?.search ? Boolean(parseSearch(atSearch, search)) : !!api,
      ...(api?.options ?? {}),
    }
  );
  const selectOptions = data && api ? api.responseTransform(data) : options;
  const inputChangeApi = useCallback(
    debounce((newInputValue: string) => {
      setSearch(newInputValue);
    }, 250),
    []
  );

  // limit amount of options shown
  const defaultFilterOptions = createFilterOptions<ComboBoxOption>();
  const filterOptions = (opts: ComboBoxOption[], state: FilterOptionsState<ComboBoxOption>) => {
    if (atSearch) {
      const searchValue = parseSearch(atSearch, state.inputValue);
      if (!searchValue) return [];
      return defaultFilterOptions(opts, { ...state, inputValue: searchValue }).slice(
        0,
        optionsLimit
      );
    }
    return defaultFilterOptions(opts, state).slice(0, optionsLimit);
  };

  return (
    <Autocomplete
      {...rest}
      selectOnFocus
      clearOnBlur={clearOnBlur}
      freeSolo={freeSolo}
      fullWidth={fullWidth}
      openText={openText}
      open={open}
      loading={loading}
      loadingText={loadingText}
      componentsProps={{
        paper: {
          sx: {
            minWidth: 250,
          },
        },
      }}
      noOptionsText={noOptionsText}
      options={selectOptions}
      groupBy={groupBy ? option => (option as ComboBoxOption).groupByHeader || '' : undefined}
      renderGroup={params =>
        groupBy ? (
          <li key={params.key}>
            <GroupHeader>{params.group}</GroupHeader>
            <GroupItems>{params.children}</GroupItems>
          </li>
        ) : (
          ''
        )
      }
      sx={{
        '.MuiInputBase-root': { padding: 0, input: { padding: '10px 14px' } },
        '.MuiFormControl-root': {
          '.MuiInputBase-root': {
            input: { padding: '10px 14px' },
          },
        },
        ...sx,
      }}
      onClose={onClose}
      getOptionDisabled={({ disabled = false }: ComboBoxOption) => disabled}
      getOptionLabel={option => {
        if (typeof option === 'string') return option;
        if (option?.handleClick) return '';
        return option?.label;
      }}
      filterOptions={
        optionsLimit ? filterOptions : optionsValues => optionsValues as ComboBoxOption[]
      }
      onInputChange={(event: React.SyntheticEvent, newInputValue: string, reason: string) => {
        if (onInputChange) onInputChange(event, newInputValue, reason);
        if (api?.search) inputChangeApi(newInputValue);
      }}
      onChange={(event, values, reason) => {
        onChange?.(event, values, reason);
      }}
      onBlur={event => {
        onBlur?.();
      }}
      renderOption={(prop, option) => {
        return (
          <Tooltip title={option.tooltip || ''}>
            <Li {...prop} key={option.id}>
              {option.CustomElement ? (
                <option.CustomElement
                  label={option.label}
                  handleClick={option.handleClick}
                  {...option.customProps}
                />
              ) : (
                <>
                  {hasAvatar ? (
                    <>
                      <Avatar
                        size="sm"
                        letters={option.label}
                        color={option.avatarColor ? `#${option.avatarColor}` : ''}
                      />
                      <Spacing />
                    </>
                  ) : null}
                  <span onClick={option.handleClick}>{option.label}</span>
                </>
              )}
            </Li>
          </Tooltip>
        );
      }}
      renderInput={params => {
        const selected = options.find(o => o.label === rest?.value);
        if (selected?.CustomElement) {
          return (
            <>
              <HiddenInput>
                <TextInput
                  {...(rest as TextInputProps)}
                  {...(params as TextInputProps)}
                  variant={inputVariant === 'unstyled' ? 'standard' : inputVariant}
                  InputProps={{ ...params.InputProps, ...InputProps }}
                />
              </HiddenInput>
              <selected.CustomElement label={selected.label} {...selected.customProps} />
            </>
          );
        }
        const textParams = {
          ...params,
          InputLabelProps: { ...params.InputLabelProps, shrink: true, required: rest.required },
        };
        return (
          <TextInput
            autoFocus={Boolean(open)}
            {...textParams}
            error={error}
            color={error ? 'error' : 'primary'}
            placeholder={loading ? loadingText : placeholder}
            label={label}
            helperText={error ? label : ''}
            {...(rest as TextInputProps)}
            variant={inputVariant === 'unstyled' ? 'standard' : inputVariant}
            InputProps={{ ...params.InputProps, ...InputProps }}
            onKeyDown={e => {
              // for some reason onKeyDown don't work in certain scenarios
              if (onKey) onKey(e);
              if (onKeyDown) onKeyDown(e);
            }}
          />
        );
      }}
    />
  );
};

const Li = styled.li`
  font-size: 0.875rem;
`;

export const GroupHeader = styled('div')(({ theme }) => ({
  ...theme.typography.bodyXs,
  fontWeight: 600,
  position: 'sticky',
  top: '-8px',
  padding: '8px 10px',
  color: theme.palette.text.placeholder,
  zIndex: 99,
}));

export const GroupItems = styled('ul')({
  padding: 0,
});

export const HiddenInput = styled.span`
  visibility: hidden;
  width: 0px;
  display: block;
  height: 0px;
`;

const Spacing = styled.span`
  min-width: 15px;
  height: 15px;
`;
